import { useCallback, useMemo } from 'react';
import { useCachedList } from '../../../../hooks/useCachedList';
import { gutDataCache, serializeGutDataKey } from '../../../../utils/listCache/caches';
import { addDays, localToday } from '../model/dates';
import { fetchBowelLogs, fetchGutSymptoms } from './gutApi';

// One year back: the longest stretch the week selector can reach, and the
// raw-fetch cap (the server refuses a range of 366 days or more).
export const HISTORY_DAYS = 365;

/**
 * Loads the client's gut logs: a year of symptom and bowel rows, in parallel.
 * Fibre is loaded separately, a month at a time (useGutFibre), because a
 * year of diet tracking takes ~20 s.
 *
 * @param {string} userId - The client's UserProfile id.
 * @returns {{ data: object|undefined, today: string, loading: boolean, error: any, refresh: () => Promise<void> }}
 */
export function useGutData(userId) {
  const today = useMemo(() => localToday(), []);
  const key = userId ? serializeGutDataKey(userId, today) : null;

  const fetcher = useCallback(async () => {
    const start = addDays(today, -HISTORY_DAYS);
    const [symptoms, bowel] = await Promise.all([
      fetchGutSymptoms(userId, start, today),
      fetchBowelLogs(userId, start, today),
    ]);
    return { today, historyStart: start, symptoms, bowel };
  }, [userId, today]);

  const { data, loading, error, refresh } = useCachedList({
    cache: gutDataCache,
    key,
    fetcher,
    enabled: Boolean(userId),
  });

  return { data, today, loading, error, refresh };
}
